import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import SlaBeacon from "./SlaBeacon";

export default function QuoteSlaAlertPopup() {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [ticked, setTicked] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get("/quote-sla/alerts/pending")
      .then(({ data }) => {
        if ((data.alerts || []).length) { setAlerts(data.alerts); setOpen(true); }
      })
      .catch(() => {});
  }, []);

  const acknowledge = async () => {
    setSaving(true);
    try {
      await API.post("/quote-sla/alerts/acknowledge", { alert_ids: alerts.map(a => a.id) });
      toast.success("Missed SLAs acknowledged — logged against your name");
      setOpen(false);
    } catch { toast.error("Failed to acknowledge"); }
    finally { setSaving(false); }
  };

  if (!alerts.length) return null;
  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-lg [&>button]:hidden"
        data-testid="sla-alert-popup"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <Warning size={20} weight="fill" /> Missed Quote SLAs — Action Required
          </DialogTitle>
          <DialogDescription>
            The following job requests have breached their quote SLAs. You must acknowledge you have read this before continuing.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-64 overflow-y-auto space-y-2" data-testid="sla-alert-list">
          {alerts.map(a => (
            <div key={a.id} className={`flex items-center gap-3 p-3 rounded-md border ${a.level === "red" ? "border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-800" : "border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800"}`}>
              <SlaBeacon level={a.level} />
              <div className="min-w-0 flex-1">
                <Link to={`/job-requests/${a.jr_id}`} className="text-sm font-semibold hover:underline truncate block">
                  {a.reference} — {a.title}
                </Link>
                <p className="text-xs text-muted-foreground">
                  {a.level === "red" ? "4+ working days — quote not sent to client" : "2+ working days — no quote added"}
                  {" · "}triggered {new Date(a.triggered_at).toLocaleDateString("en-GB")}
                </p>
              </div>
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${a.level === "red" ? "bg-red-600 text-white" : "bg-amber-500 text-white"}`}>{a.level}</span>
            </div>
          ))}
        </div>
        <label className="flex items-start gap-2.5 p-3 rounded-md border border-border bg-muted/40 cursor-pointer">
          <Checkbox checked={ticked} onCheckedChange={v => setTicked(!!v)} className="mt-0.5" data-testid="sla-ack-checkbox" />
          <span className="text-xs leading-relaxed">
            I confirm I have read and acknowledge these missed SLAs. My acknowledgment will be logged with my name and timestamp.
          </span>
        </label>
        <DialogFooter>
          <Button onClick={acknowledge} disabled={!ticked || saving} className="w-full" data-testid="sla-ack-confirm-btn">
            {saving ? "Saving..." : `Acknowledge ${alerts.length} alert${alerts.length !== 1 ? "s" : ""} & Close`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
